import React, { useState, useEffect, useMemo } from 'react';
import PropTypes from 'prop-types';
import {
    Box,
    Typography,
    Accordion,
    AccordionSummary,
    AccordionDetails,
    Button,
    TextField,
    makeStyles
} from '@material-ui/core';
import { Autocomplete } from '@material-ui/lab';
import ExpandMoreIcon from '@material-ui/icons/ExpandMore';
import { useSelector } from 'react-redux';
import moment from 'moment';
import CustomerApi from '../../../../api/customerApi';
import InvoiceApi from '../../../../api/invoiceApi';
import InvoiceApiCC from '../../../../collums-components/api/InvoiceApi';
import { listActiveClinics } from '../../../../collums-components/api/ClinicApi';
import { getAllFormsByType } from '../../../../collums-components/api/FormApi';
import queryString from 'query-string';
import customDropdownSearch from '../../../../collums-components/helpers/customDropdownSearch';
import { getCurrencySymbol } from '../../../../collums-components/helpers';
import {
    getCurrentUserSelector,
    getCurrentClinicSelector,
    getOrganisationSelector
} from '../../../../customSelectors/calendar';

const useStyles = makeStyles(() => ({
    sidebar: {
        overflowY: 'auto',
        '&::-webkit-scrollbar': {
            width: 4
        },
        '&::-webkit-scrollbar-thumb': {
            background: '#c1c1c199',
            borderRadius: 2
        }
    },
    accordion: {
        boxShadow: 'none',
        border: '1px solid rgba(0,0,0,0.12)',
        '&:before': {
            display: 'none'
        },
        '&.Mui-expanded': {
            margin: 0
        }
    },
    accordionSummary: {
        minHeight: '36px !important',
        padding: '0 12px',
        '& .MuiAccordionSummary-content': {
            margin: '8px 0'
        },
        '& .MuiAccordionSummary-content.Mui-expanded': {
            margin: '8px 0'
        }
    },
    accordionDetails: {
        padding: '0 4px 8px',
        flexDirection: 'column'
    },
    sectionTitle: {
        fontSize: 13,
        fontWeight: 500
    },
    tagButton: {
        textTransform: 'none',
        fontSize: 12,
        justifyContent: 'flex-start',
        padding: '2px 8px',
        minHeight: 28,
        width: '100%',
        textAlign: 'left',
        '&:hover': {
            background: '#f5f5f5'
        }
    },
    dropdown: {
        marginBottom: 4,
        '& .MuiInputBase-root': {
            fontSize: 12
        },
        '& .MuiAutocomplete-input': {
            padding: '4px !important'
        },
        '& .MuiInputLabel-root': {
            fontSize: 12
        }
    }
}));

const MergeTagsSidebar = ({ customer, onInsert, onInsertForm, showForms, initialInvoiceId }) => {
    const classes = useStyles();
    const currentUser = useSelector(getCurrentUserSelector);
    const currentClinic = useSelector(getCurrentClinicSelector);
    const organisation = useSelector(getOrganisationSelector);

    const [appointments, setAppointments] = useState([]);
    const [selectedAppointment, setSelectedAppointment] = useState(null);
    const [invoices, setInvoices] = useState([]);
    const [selectedInvoice, setSelectedInvoice] = useState(null);
    const [invoicePreview, setInvoicePreview] = useState(null);
    const [clinics, setClinics] = useState([]);
    const [selectedClinic, setSelectedClinic] = useState(null);
    const [isMultiLocation, setIsMultiLocation] = useState(false);
    const [availableForms, setAvailableForms] = useState([]);

    const customerId = customer?.id;
    const selectedInvoiceId = selectedInvoice?.id;

    useEffect(() => {
        if (!customerId) return;

        CustomerApi.listAppointmentsByCustomer(customerId, {
            pageSize: 200,
            orderBy: '-event.start'
        })
            .then(res => {
                const appts = res?.appointments || [];
                setAppointments(appts);
            })
            .catch(() => {});

        InvoiceApi.getAllInvoices(customerId)
            .then(res => {
                const invs = res?.data || res || [];
                invs.sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));
                setInvoices(invs);
            })
            .catch(() => {});

        listActiveClinics()
            .then(res => {
                const list = res || [];
                setClinics(list);
                setIsMultiLocation(list.length > 1);
                if (list.length === 1) {
                    setSelectedClinic(list[0]);
                }
            })
            .catch(() => {});

        if (showForms) {
            getAllFormsByType(queryString.stringify({ type: 'All' }))
                .then(res => setAvailableForms(res || []))
                .catch(() => {});
        }
    }, [customerId, showForms]);

    useEffect(() => {
        if (initialInvoiceId && invoices.length > 0 && !selectedInvoice) {
            const match = invoices.find(inv => inv.id === initialInvoiceId || inv._id === initialInvoiceId);
            if (match) {
                setSelectedInvoice(match);
            }
        }
    }, [initialInvoiceId, invoices]); // eslint-disable-line react-hooks/exhaustive-deps

    const nextAppointment = useMemo(() => {
        const now = new Date();
        const future = appointments.filter(a => new Date(a.event?.start) > now);
        return future.length > 0 ? future[future.length - 1] : null;
    }, [appointments]);

    const latestInvoice = useMemo(() => {
        return invoices.length > 0 ? invoices[0] : null;
    }, [invoices]);

    const invoiceIdForPreview = selectedInvoiceId || latestInvoice?.id;

    useEffect(() => {
        if (!invoiceIdForPreview) {
            setInvoicePreview(null);
            return;
        }
        InvoiceApiCC.getEmailPreview(invoiceIdForPreview)
            .then(res => {
                setInvoicePreview(res);
            })
            .catch(() => {});
    }, [invoiceIdForPreview]);

    const getApptForTags = () => selectedAppointment || nextAppointment;
    const getInvoiceForTags = () => selectedInvoice || latestInvoice;

    const formatApptLabel = appt => {
        if (!appt) return '';
        const date = moment(appt.event?.start).format('DD/M/YY');
        const time = moment(appt.event?.start).format('HH:mm');
        const service = appt.service?.name || '';
        return `${date} ${time} ${service}`;
    };

    const formatInvoiceLabel = inv => {
        if (!inv) return '';
        const date = moment(inv.createdAt).format('D/M/YY');
        const code = (inv.code || inv.originalCode || '').replace(/^0+/, '');
        const currency = localStorage.getItem('currencySymbol') || localStorage.getItem('currency') || '£';
        const total = inv.amount != null ? `${currency}${Number(inv.amount).toFixed(2)}` : '';
        return `${date} INV${code} ${total}`;
    };

    const getFullAddress = () => {
        const country = customer?.country?.label || customer?.country;
        const parts = [
            customer?.address1,
            customer?.address2,
            customer?.city,
            customer?.county,
            customer?.postCode,
            typeof country === 'string' ? country : ''
        ].filter(Boolean);
        return parts.join(', ');
    };

    const getGPFullAddress = () => {
        const gp = customer?.generalPractitioner;
        if (!gp) return '';
        const parts = [
            gp.address1,
            gp.address2,
            gp.city,
            gp.county,
            gp.postCode,
            gp.country?.label || gp.country
        ].filter(Boolean);
        return parts.join(', ');
    };

    const getClinicAddress = () => {
        const clinic = selectedClinic || currentClinic;
        if (!clinic?.address) return '';
        const addr = clinic.address;
        const parts = [addr.line1, addr.line2, addr.line3, addr.city, addr.county, addr.postcode, addr.country].filter(
            Boolean
        );
        return parts.join(', ');
    };

    const getServiceList = (appt, includeTime, includePrice, includeStaff) => {
        if (!appt) return '';
        const parts = [];
        const serviceName = appt.service?.name || '';
        if (serviceName) {
            let entry = serviceName;
            if (includeTime) {
                entry += ` ${moment(appt.event?.start).format('HH:mm')}-${moment(appt.event?.end).format('HH:mm')}`;
            }
            if (includePrice) {
                const currency = localStorage.getItem('currencySymbol') || localStorage.getItem('currency') || '£';
                entry += ` ${currency}${Number(appt.grossPrice || appt.price || 0).toFixed(2)}`;
            }
            if (includeStaff) {
                const staff = appt.practitioner?.displayName || appt.practitioner?.firstName || '';
                if (staff) entry += ` (${staff})`;
            }
            parts.push(entry);
        }
        return parts.join(', ');
    };

    const handleTagClick = value => {
        if (value != null && value !== '' && value !== undefined) {
            onInsert(String(value));
        }
    };

    const renderTagButton = (label, value) => (
        <Button
            key={label}
            className={classes.tagButton}
            onClick={() => handleTagClick(value)}
            title={String(value || '')}
        >
            {label}
        </Button>
    );

    const appt = getApptForTags();
    const invoice = getInvoiceForTags();
    const clinic = selectedClinic || currentClinic;
    const gp = customer?.generalPractitioner;
    const currency = getCurrencySymbol();

    const sections = [
        {
            title: 'General',
            defaultExpanded: true,
            tags: [['Today Date', moment().format('DD/MM/YYYY')]]
        },
        {
            title: 'Client Details',
            tags: [
                ['Client ID', customer?.friendlyId || customer?.id],
                ['Client Title', customer?.title],
                ['Client First Name', customer?.firstName],
                ['Client Middle Name', customer?.middleName],
                ['Client Surname', customer?.surname],
                ['Client DOB', customer?.dateOfBirth ? moment(customer.dateOfBirth).format('DD/MM/YYYY') : ''],
                ['Client Email', customer?.email],
                ['Client Mobile', customer?.mobilePhone],
                ['Client Full Address', getFullAddress()],
                ['Client Address Line 1', customer?.address1],
                ['Client Address Line 2', customer?.address2],
                ['Client City', customer?.city],
                ['Client Postcode', customer?.postCode],
                ['Client NHS No', customer?.nhsNumber],
                ['Client Height', customer?.height],
                ['Client Weight', customer?.weight]
            ]
        },
        {
            title: 'Appt Details',
            hasDropdown: 'appointment',
            tags: [
                ['Appt Day', appt ? moment(appt.event?.start).format('dddd') : ''],
                ['Appt Date', appt ? moment(appt.event?.start).format('DD/MM/YYYY') : ''],
                ['Appt Start', appt ? moment(appt.event?.start).format('HH:mm') : ''],
                ['Appt End', appt ? moment(appt.event?.end).format('HH:mm') : ''],
                ['Service', appt?.service?.name || ''],
                ['Staff', appt?.practitioner?.displayName || appt?.practitioner?.firstName || ''],
                ['Visit Start', appt ? moment(appt.event?.start).format('HH:mm') : ''],
                ['Visit End', appt ? moment(appt.event?.end).format('HH:mm') : ''],
                ['Service Time Price Staff List', getServiceList(appt, true, true, true)],
                ['Service Time Staff List', getServiceList(appt, true, false, true)],
                ['Service Staff List', getServiceList(appt, false, false, true)],
                ['Service List', getServiceList(appt, false, false, false)]
            ]
        },
        {
            title: 'Invoice Details',
            hasDropdown: 'invoice',
            tags: [
                ['Invoice No', invoice?.code?.replace(/^0+/, '') || invoice?.originalCode?.replace(/^0+/, '') || ''],
                ['Invoice Date', invoice ? moment(invoice.createdAt).format('DD/MM/YYYY') : ''],
                ['Invoice Total', invoice?.amount != null ? `${currency}${Number(invoice.amount).toFixed(2)}` : ''],
                [
                    'Invoice Paid Amount',
                    (() => {
                        if (!invoice?.payments) return '';
                        const paid = invoice.payments.reduce((sum, p) => sum + (p.amount || 0), 0);
                        return `${currency}${paid.toFixed(2)}`;
                    })()
                ],
                [
                    'Invoice Os Amount',
                    (() => {
                        if (!invoice) return '';
                        const paid = (invoice.payments || []).reduce((sum, p) => sum + (p.amount || 0), 0);
                        return `${currency}${(Number(invoice.amount || 0) - paid).toFixed(2)}`;
                    })()
                ],
                ['Invoice', invoicePreview?.invoiceDetails || '']
            ]
        },
        {
            title: 'Staff',
            tags: [
                ['Staff Title', currentUser?.title],
                ['Staff First Name', currentUser?.firstName],
                ['Staff Middle Name', currentUser?.middleName],
                ['Staff Last Name', currentUser?.surname],
                ['Staff Display Name', currentUser?.displayName],
                ['Staff Job Title', currentUser?.jobTitle],
                [
                    'Staff Signature',
                    currentUser?.signature
                        ? `<img src="${currentUser.signature}" alt="Signature" style="max-width: 200px;" />`
                        : ''
                ]
            ]
        },
        {
            title: 'Clinic Details',
            hasDropdown: isMultiLocation ? 'clinic' : null,
            tags: [
                ['Clinic Name', clinic?.accountName || clinic?.organisationName || organisation?.name || ''],
                ['Clinic Address', getClinicAddress()],
                ['Clinic Email', clinic?.publicEmail || organisation?.email || ''],
                ['Clinic Tel', clinic?.publicPhone || ''],
                [
                    'Clinic Logo',
                    organisation?.logo
                        ? `<img src="${organisation.logo}" alt="Clinic Logo" style="max-width: 200px;" />`
                        : ''
                ]
            ]
        },
        {
            title: 'GP Details',
            tags: [
                ['GP Name', gp?.name || ''],
                ['GP Practice Name', gp?.practiceName || ''],
                ['GP Full Address', getGPFullAddress()],
                ['GP Phone', gp?.tel || ''],
                ['GP Email', gp?.email || '']
            ]
        },
        ...(showForms
            ? [
                  {
                      title: 'Forms',
                      hasDropdown: 'form',
                      tags: []
                  }
              ]
            : [])
    ];

    return (
        <Box className={classes.sidebar} style={{ height: '100%' }}>
            <Typography
                style={{
                    fontSize: 14,
                    fontWeight: 500,
                    textAlign: 'center',
                    padding: '8px 0'
                }}
            >
                Merge Tags
            </Typography>
            {sections
                .filter(s => s.title !== 'Forms')
                .map(section => (
                    <Accordion
                        key={section.title}
                        className={classes.accordion}
                        defaultExpanded={section.defaultExpanded || false}
                    >
                        <AccordionSummary expandIcon={<ExpandMoreIcon />} className={classes.accordionSummary}>
                            <Typography className={classes.sectionTitle}>{section.title}</Typography>
                        </AccordionSummary>
                        <AccordionDetails className={classes.accordionDetails}>
                            {section.hasDropdown === 'appointment' && (
                                <Autocomplete
                                    className={classes.dropdown}
                                    size="small"
                                    options={appointments}
                                    getOptionLabel={formatApptLabel}
                                    value={selectedAppointment}
                                    onChange={(_, val) => setSelectedAppointment(val)}
                                    renderInput={params => (
                                        <TextField {...params} variant="outlined" placeholder="Select appointment" />
                                    )}
                                    renderOption={option => (
                                        <Typography style={{ fontSize: 12 }}>{formatApptLabel(option)}</Typography>
                                    )}
                                />
                            )}
                            {section.hasDropdown === 'invoice' && (
                                <Autocomplete
                                    className={classes.dropdown}
                                    size="small"
                                    options={invoices}
                                    getOptionLabel={formatInvoiceLabel}
                                    value={selectedInvoice}
                                    onChange={(_, val) => setSelectedInvoice(val)}
                                    renderInput={params => (
                                        <TextField {...params} variant="outlined" placeholder="Select invoice" />
                                    )}
                                    renderOption={option => (
                                        <Typography style={{ fontSize: 12 }}>{formatInvoiceLabel(option)}</Typography>
                                    )}
                                />
                            )}
                            {section.hasDropdown === 'clinic' && (
                                <Autocomplete
                                    className={classes.dropdown}
                                    size="small"
                                    options={clinics}
                                    getOptionLabel={c => c.accountName || c.organisationName || ''}
                                    value={selectedClinic}
                                    onChange={(_, val) => setSelectedClinic(val)}
                                    renderInput={params => (
                                        <TextField {...params} variant="outlined" placeholder="Select clinic" />
                                    )}
                                    renderOption={option => (
                                        <Typography style={{ fontSize: 12 }}>
                                            {option.accountName || option.organisationName}
                                        </Typography>
                                    )}
                                />
                            )}
                            {section.tags.map(([label, value]) => renderTagButton(label, value))}
                        </AccordionDetails>
                    </Accordion>
                ))}
            {showForms && (
                <Box className={classes.accordion} style={{ padding: '8px 4px' }}>
                    <Typography className={classes.sectionTitle} style={{ padding: '0 8px 4px' }}>
                        Forms
                    </Typography>
                    <Autocomplete
                        className={classes.dropdown}
                        size="small"
                        options={[...availableForms].sort((a, b) =>
                            (a.name || '').toLowerCase().localeCompare((b.name || '').toLowerCase())
                        )}
                        getOptionLabel={option => option.name || ''}
                        value={null}
                        onChange={(_, val) => {
                            if (val && onInsertForm) {
                                onInsertForm(val);
                            }
                        }}
                        filterOptions={customDropdownSearch}
                        renderInput={params => (
                            <TextField {...params} variant="outlined" placeholder="Select form to insert" />
                        )}
                        renderOption={option => <Typography style={{ fontSize: 12 }}>{option.name}</Typography>}
                    />
                </Box>
            )}
        </Box>
    );
};

MergeTagsSidebar.propTypes = {
    customer: PropTypes.object,
    onInsert: PropTypes.func.isRequired,
    onInsertForm: PropTypes.func,
    showForms: PropTypes.bool,
    initialInvoiceId: PropTypes.string
};

export default MergeTagsSidebar;
